import React from 'react';
import { Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import { Caption, VideoTheme, Word as WordType } from '../../types';
import { EmojiDisplay } from './EmojiDisplay';
import { Word } from './Word';

interface BeastProps {
  caption: Caption;
  currentFrame: number;
  theme: VideoTheme;
  fontSize: number;
  captionPosY: number;
  highlightColors: string[];
  gap: number;
  emojiAnimationData: any;
}

export const Beast: React.FC<BeastProps> = ({
  caption,
  currentFrame,
  theme,
  fontSize,
  captionPosY,
  highlightColors,
  gap,
  emojiAnimationData,
}) => {
  const { fps } = useVideoConfig();
  const { emoji, words } = caption;

  const frame = useCurrentFrame();

  const scale = interpolate(frame, [0, 0.5, 3], [1, 1.2, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
    easing: Easing.bezier(0.08, 0.68, 0.74, 0.71),
  });

  return (
    <div
      id="beast-container"
      style={{
        ...styles.container,
        bottom: `${captionPosY}px`,
        transform: `scale(${scale})`,
        gap: `${gap}px`,
      }}
    >
      <EmojiDisplay emoji={emoji} theme={theme} emojiAnimationData={emojiAnimationData} />
      <WordList
        words={words}
        currentFrame={currentFrame}
        fps={fps}
        highlightColors={highlightColors}
        theme={theme}
        fontSize={fontSize}
      />
    </div>
  );
};

// --------------------------------------------------------------------------------

interface WordListProps {
  words: WordType[];
  currentFrame: number;
  fps: number;
  highlightColors: string[];
  theme: VideoTheme;
  fontSize: number;
}

const WordList: React.FC<WordListProps> = ({
  words,
  currentFrame,
  fps,
  highlightColors,
  theme,
  fontSize,
}) => (
  <>
    {words.map((wordObj, index) => {
      const { word, highlightColorId, lineBreak } = wordObj;

      const highLightColor = highlightColorId ? highlightColors[Number(highlightColorId)] : '';

      return (
        <React.Fragment key={index}>
          {lineBreak && <div style={{ flex: '100%' }} />}
          <Word
            word={word}
            color={highLightColor || theme.fontStyle.fontColor}
            fontSize={fontSize}
            theme={theme}
            customWordStyle={{
              textShadow: `0 0 70px ${theme.fontStyle.fontColor}`,
              position: 'relative',
              '&::before': {
                content: `"${word}"`,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                color: 'transparent',
                WebkitTextStroke: '0.2em #000',
                zIndex: -1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              },
              '&::after': {
                content: `"${word}"`,
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                color: 'transparent',
                WebkitTextStroke: '0.2em #000',
                zIndex: -1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              },
            }}
          />
        </React.Fragment>
      );
    })}
  </>
);

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'absolute',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    top: undefined,
    lineHeight: '1',
    width: '100%',
    padding: '0px 15px',
  },
};
